import { gql, useQuery } from '@apollo/client';

import {
  ChartFragments,
  MonthlyTableFragments,
} from 'features/drilldown/cashflows/gql/cashflowDataFragments';
import type { MonthlyTableCashFlowMonth } from 'features/drilldown/cashflows/gql/__generated__/cashflowDataFragments';
import type {
  GetDealRemittanceCashflowProjection,
  GetDealRemittanceCashflowProjectionVariables,
} from './__generated__/useDealRemittanceCashflowProjection';
/**
 * Forward-looking cashflow projection for a remittance deal. The api resolver derives
 * the upload_id server-side from the deal's latest approved remittance upload, so the
 * client only needs to pass `dealId`. Returns empty arrays when the deal has no
 * approved upload yet (`remittance_performance_summary` → null).
 *
 * Uses zero-assumption defaults (CDR = CPR = LSR = 0) for the initial wiring; the
 * RemittanceCashFlow page keeps `hideModelingInputs={true}`. Exposing user-tunable
 * modeling inputs is explicit follow-up work on ENG-2464.
 *
 * Two parallel series come back:
 * - `monthlyCashFlows` — the caller's investor-share series. Sum matches the buyer's
 *   expected wire-in amounts each month.
 * - `fullMonthlyCashFlows` — the 100% tape series (pool-level). Added in ENG-2659 so
 *   both buyer and seller can see the same underlying pool cashflow on the chart;
 *   servicer view = `full − investor` on a per-field basis, computed client-side.
 *
 * `fullMonthlyCashFlows` may be null/empty on responses from older cashflow Lambda
 * instances during a rolling deploy (`cashflows#138` rollout). Callers should treat
 * it as optional.
 */
export const GET_DEAL_REMITTANCE_CASHFLOW_PROJECTION = gql`
  query GetDealRemittanceCashflowProjection($dealId: ID!) {
    deal(id: $dealId) {
      id
      remittance_performance_summary {
        cashFlows: cash_flows(
          conditionalDefaultRate: 0
          conditionalPrepaymentRate: 0
          lossSeverityRate: 0
          servicingRate: 0
          daysDelay: 0
          rateType: Sofr
          prepaymentAssumptionType: CPR
          defaultAssumptionType: CDR
        ) {
          monthlyCashFlows: monthly_cash_flows {
            ...ChartCashFlowMonth
            ...MonthlyTableCashFlowMonth
          }
          fullMonthlyCashFlows: full_monthly_cash_flows {
            ...ChartCashFlowMonth
            ...MonthlyTableCashFlowMonth
          }
        }
      }
    }
  }
  ${MonthlyTableFragments.cashFlowMonth}
  ${ChartFragments.cashFlowMonth}
`;

export function useDealRemittanceCashflowProjection(dealId: string | undefined) {
  const skip = !dealId?.trim();
  const { data, loading, error } = useQuery<
    GetDealRemittanceCashflowProjection,
    GetDealRemittanceCashflowProjectionVariables
  >(GET_DEAL_REMITTANCE_CASHFLOW_PROJECTION, {
    variables: { dealId: dealId ?? '' },
    skip,
    // Match `useGetDealRemittanceSummaries` (network-only). Both datasets get merged
    // into one chart via `mergePastAndProjected`, so their freshness must agree —
    // otherwise a return visit splices fresh actuals with a stale projection.
    fetchPolicy: 'network-only',
  });

  const cashFlows = data?.deal?.remittance_performance_summary?.cashFlows;
  const monthlyCashFlows: MonthlyTableCashFlowMonth[] = cashFlows?.monthlyCashFlows ?? [];
  const fullMonthlyCashFlows: MonthlyTableCashFlowMonth[] = cashFlows?.fullMonthlyCashFlows ?? [];

  return {
    monthlyCashFlows,
    fullMonthlyCashFlows,
    loading,
    error,
  };
}
